// app_client/src/store/useProductStore.js
import { create } from 'zustand';
import axios from 'axios';
import { buildListQueryKey, normalizeListQueryParams } from '../hooks/useListQuery';

const useProductStore = create((set, get) => ({
  products: [],
  loading: false,
  pagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    limit: 10
  },
  lastParams: {}, // Yenileme (refresh) işlemleri için filtreleri saklar
  lastParamsKey: '',

  // 1. Ürünleri Listele (Arama ve Filtreleme Dahil)
  fetchProducts: async (params = {}, options = {}) => {
    const nextParams = normalizeListQueryParams(params, params.page || 1);
    const nextParamsKey = buildListQueryKey(nextParams, nextParams.page);
    const persistQuery = options.persistQuery !== false;
    const { loading, lastParamsKey } = get();

    if (loading && lastParamsKey === nextParamsKey) {
      return;
    }

    set((state) => ({
      loading: true,
      ...(persistQuery ? {
        lastParams: nextParams,
        lastParamsKey: nextParamsKey
      } : {
        lastParams: state.lastParams,
        lastParamsKey: state.lastParamsKey
      })
    }));
    try {
      // API'ye gönderilecek query string oluşturma
      // params: { search, type, status, page }
      const response = await axios.get('/api/products', { params: nextParams });
      
      set({ 
        products: response.data.items,
        pagination: {
          totalItems: response.data.total || 0,
          currentPage: response.data.page || 1,
          totalPages: response.data.totalPages || 1,
          limit: nextParams.limit || 10
        },
        loading: false 
      });
    } catch (error) {
      set({ loading: false });
      console.error("Ürünler getirilirken hata oluştu:", error);
    }
  },

  // 2. Ürün Durumunu Güncelle (Aktif <-> Pasif)
  toggleProductStatus: async (id, newStatus) => {
    try {
      await axios.patch(`/api/products/${id}/status`, { isActive: newStatus });
      
      // Local state'i hızlıca güncelle (Optimistic Update)
      set((state) => ({
        products: state.products.map((p) => 
          p._id === id ? { ...p, isActive: newStatus } : p
        )
      }));
      return true;
    } catch (error) {
      console.error("Durum güncellenemedi:", error);
      return false;
    }
  },

  // 3. Ürünü Sil (Hata Yönetimi ile)
  deleteProduct: async (id) => {
    try {
      const response = await axios.delete(`/api/products/${id}`);
      
      if (response.data.success) {
        const { products, pagination } = get();
        const nextPage = products.length === 1 && pagination.currentPage > 1
          ? pagination.currentPage - 1
          : pagination.currentPage;

        return { success: true, nextPage };
      }
      return { success: false, message: response.data?.message || "Silme işlemi başarısız." };
    } catch (error) {
      // Backend'den gelen "Faturada kullanılıyor" mesajını yakala
      const message = error.response?.data?.message || "Silme işlemi başarısız.";
      return { success: false, message };
    }
  },

  // 4. Tekil Ürün Getir (Düzenleme sayfası için)
  fetchProductById: async (id) => {
    if (!id || id === 'new') {
      return null;
    }

    try {
      const response = await axios.get(`/api/products/${id}`);
      return response.data.data;
    } catch (error) {
      console.error("Ürün detayı getirilemedi:", error);
      return null;
    }
  },

  // 5. Kaydet veya Güncelle
  saveProduct: async (formData, id = null) => {
    try {
      let response;
      if (id) {
        response = await axios.put(`/api/products/${id}`, formData);
      } else {
        response = await axios.post('/api/products', formData);
      }
      
      return { success: true, data: response.data.data };
    } catch (error) {
      const message = error.response?.data?.message || "Kayıt başarısız.";
      return { success: false, message };
    }
  },

  checkCodeAvailability: async (code, excludeId = null) => {
    try {
      const res = await axios.get(`/api/products/check-code`, { 
        params: { code, excludeId } 
      });
      return res.data.isTaken; 
    } catch {
      return false;
    }
  },

  suggestProductCode: async (type) => {
    try {
      // Backend controller'ın beklediği 'type' query olarak gönderiliyor
      const response = await axios.get('/api/products/generate-code', { 
        params: { type } 
      });
      
      if (response.data.success) {
        return response.data.code; // Örn: "STK-005"
      }
      return '';
    } catch (error) {
      console.error("Kod önerisi alınamadı:", error);
      return '';
    }
  }
}));

export default useProductStore;
